import { useContext, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Label as LabelPrimitive } from '@givebutter/primitives/label';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { faTag } from '@fortawesome/pro-regular-svg-icons';
import { fetchCustomField } from 'api/customFields';
import { CustomFieldFilter } from 'components/filter/filters-next/CustomFieldFilter';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext, IFilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import {
    CustomFieldFilterFormValues,
    CustomFieldValue,
} from 'components/filter/filters-next/custom-field-types';
import {
    getDefaultFilterOperatorByType,
    getDefaultValueByType,
    transformOperatorReadableByType,
    transformReadableByType,
    transformUpstreamByType,
} from 'components/filter/filters-next/transformCustomFields';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    DownstreamPreprocessTransform,
    FilterIcon,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import * as Form from 'components/form';
import * as Input from 'components/input';
import { ListItem } from 'components/list-item';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCustomFields } from 'customHooks/useInfiniteCustomFields';
import { identity } from 'lodash';
import { customFieldQueryKeys } from 'queries/custom-field-query-keys';
import { CustomField, CustomFieldGroup } from 'types/domain';

export const filterKey: ServerKey = 'custom_fields';
export const Label: LabelFilterComponent = () => 'Custom fields';

export const Icon: FilterIcon = faTag;

export const Filter: ContentFilterComponent = () => {
    const scope = useCustomFieldGroup();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const { data, ref } = useInfiniteCustomFields({
        query: debouncedSearch,
        scope,
        source: 'filters',
    });
    const customFields = data?.pages ?? [];
    const { isPending, data: cf } = useSavedCustomField();
    const filterContext = useFilter<CustomFieldValue>();
    const defaultValue = filterContext.defaultValue;
    const [selectedCustomField, setSelectedCustomField] = useState<CustomField | undefined>(
        defaultValue?.value ? cf : undefined,
    );

    const methods = Form.useForm<CustomFieldFilterFormValues>({
        defaultValues: {
            value: {
                value: defaultValue?.value.value ?? '',
                key: defaultValue?.value.key ?? 0,
                type: cf?.type ?? 'text',
            },
            filterOperator: defaultValue?.filterOperator ?? 'includes',
        },
    });
    const { path, setValue, watch } = methods;
    const filterOperator = watch('filterOperator');
    const nextPath = path(
        ['is_empty', 'is_not_empty'].includes(filterOperator ?? '')
            ? 'filterOperator'
            : 'value.value',
    );

    return (
        <LoadingOverlay size={'sm'} isLoading={isPending} delay={2000}>
            <FilterFormContext.Provider value={filterContext}>
                <FilterContainer>
                    {selectedCustomField ? (
                        <Form.Provider {...methods}>
                            <FilterForm path={nextPath}>
                                <CustomFieldFilter customField={selectedCustomField} />
                                <FilterSubmit />
                            </FilterForm>
                        </Form.Provider>
                    ) : (
                        <Form.Container>
                            <LabelPrimitive>Custom fields</LabelPrimitive>
                            <Input.Root size={'sm'}>
                                <Input.SearchIcon />
                                <Input.Form
                                    placeholder={'Search'}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </Input.Root>
                            {customFields.map(cf => {
                                return (
                                    <ListItem
                                        onClick={() => {
                                            setSelectedCustomField(cf);
                                            setValue('value.value', getDefaultValueByType(cf.type));
                                            setValue('value.key', cf.id);
                                            setValue('value.type', cf.type);
                                            setValue(
                                                'filterOperator',
                                                getDefaultFilterOperatorByType(cf.type),
                                            );
                                        }}
                                        key={cf.id}
                                    >
                                        {cf.title}
                                    </ListItem>
                                );
                            })}
                            <div ref={ref} />
                        </Form.Container>
                    )}
                </FilterContainer>
            </FilterFormContext.Provider>
        </LoadingOverlay>
    );
};

export const Readable: ReadableFilterComponent = () => {
    const { isPending, data: cf } = useSavedCustomField();
    const currentFilter = useCurrentFilter<CustomFieldValue>();

    return (
        <LoadingOverlay isLoading={isPending} size={'sm'} delay={5000}>
            <ReadableFilterValue>
                {cf.title}: {currentFilter ? transformOperatorReadableByType(currentFilter) : ''}{' '}
                {currentFilter ? transformReadableByType(currentFilter) : ''}
            </ReadableFilterValue>
        </LoadingOverlay>
    );
};

export const preprocessDownstream: DownstreamPreprocessTransform<
    CustomFieldValue,
    { key: number }
> = filter => {
    return {
        key: filter.key,
        value: filter.value,
    };
};

export const downstream: DownstreamFilterTransform<CustomField> = identity;

export const upstream: UpstreamFilterTransform<CustomFieldValue> = filter => ({
    key: Number(filter.value.key),
    ...transformUpstreamByType(filter),
});

const useSavedCustomField = () => {
    const { accountId } = useCurrentAccount();
    const currentFilter = useCurrentFilter<CustomFieldValue>();
    const nextValue = currentFilter?.value;

    return useSuspenseQuery({
        queryKey: customFieldQueryKeys.customFieldById(accountId, nextValue?.key ?? 0),
        queryFn: () => {
            if (!nextValue) {
                return Promise.resolve({} as CustomField);
            }

            return fetchCustomField(accountId, nextValue.key);
        },
    });
};

const useCustomFieldGroup = () => {
    return getGroupFromResource(useContext(FilterContext));
};

const getGroupFromResource = ({
    resource,
    relationalServerKey,
}: IFilterContext): CustomFieldGroup => {
    if (resource === 'tickets' || relationalServerKey === 'has_tickets') {
        return 'ticket';
    }

    return 'contact';
};
